// React and hooks / React y hooks
import React, { useState, useMemo, useCallback, memo } from 'react';

// Animation / Animación
import { motion } from 'framer-motion';

// Contact component - Contact form and information
// Componente Contact - Formulario de contacto e información
const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    lastName: '',
    email: '',
    phone: '',
    specialty: '',
    town: '',
    message: ''
  });

  // Estado para manejar errores de validación
  const [formErrors, setFormErrors] = useState({
    email: ''
  });

  // Handle form input changes / Manejar cambios en los inputs del formulario
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    
    // Validación para el campo de teléfono - solo permitir números
    if (name === 'phone' && !/^\d*$/.test(value)) {
      return; // No actualizar el estado si contiene caracteres no numéricos
    }
    
    // Validar formato de email
    if (name === 'email') {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
      if (value && !emailRegex.test(value)) {
        setFormErrors(prev => ({
          ...prev,
          email: 'Por favor, introduce un email válido (ejemplo: nombre@dominio.com)'
        }));
      } else {
        setFormErrors(prev => ({
          ...prev,
          email: ''
        }));
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  // Handle form submission / Manejar envío del formulario
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState({ success: false, message: '' });

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    // Validar formato de email antes de enviar
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (!emailRegex.test(formData.email)) {
      setFormErrors(prev => ({
        ...prev,
        email: 'Por favor, introduce un email válido (ejemplo: nombre@dominio.com)'
      }));
      return; // No continuar con el envío si el email no es válido
    }
    
    setIsSubmitting(true);
    setSubmitResult({ success: false, message: '' });
    
    try {
      // Usar el servidor de email local durante desarrollo
      const apiUrl = import.meta.env.PROD ? '/api/send-contact-form' : 'http://localhost:3030/api/send-email';
      console.log('Enviando formulario a:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(import.meta.env.PROD ? {
          ...formData,
          subject: `Nuevo contacto desde la web - ${formData.name} ${formData.lastName}`,
          type: 'contact_form'
        } : {
          // Formato para el servidor de email local
          to: 'viviana@vvhealthpr.com', // Email real para notificaciones
          subject: `Nuevo contacto desde la web - ${formData.name} ${formData.lastName}`,
          text: `Nombre: ${formData.name} ${formData.lastName}\nEmail: ${formData.email}\nTeléfono: ${formData.phone}\nEspecialidad: ${formData.specialty}\nPueblo: ${formData.town}\n\nMensaje:\n${formData.message}`,
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
              <h2 style="color: #333; border-bottom: 2px solid #FFCC33; padding-bottom: 10px;">Nuevo Formulario de Contacto</h2>
              
              <div style="margin: 20px 0;">
                <p><strong>Nombre:</strong> ${formData.name} ${formData.lastName}</p>
                <p><strong>Email:</strong> ${formData.email}</p>
                <p><strong>Teléfono:</strong> ${formData.phone}</p>
                <p><strong>Especialidad:</strong> ${formData.specialty}</p>
                <p><strong>Pueblo:</strong> ${formData.town}</p>
              </div>
              
              <div style="margin: 20px 0; background-color: #f9f9f9; padding: 15px; border-left: 4px solid #FFCC33; border-radius: 3px;">
                <h3 style="margin-top: 0; color: #333; font-size: 16px;">Mensaje:</h3>
                <p style="white-space: pre-line;">${formData.message}</p>
              </div>
            </div>
          `,
          // Enviar también una copia al usuario
          sendConfirmation: true,
          userEmail: formData.email
        }),
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error al enviar formulario: ${response.status} ${errorText}`);
      }
      
      const result = await response.json();
      
      if (result.success) {
        setSubmitResult({
          success: true,
          message: 'Tu mensaje ha sido enviado correctamente. Nos pondremos en contacto contigo pronto.'
        });
        // Limpiar el formulario después de enviar
        setFormData({
          name: '',
          lastName: '',
          email: '',
          phone: '',
          specialty: '',
          town: '',
          message: ''
        });
      } else {
        throw new Error(result.message || 'Error al enviar el formulario');
      }
    } catch (error) {
      console.error('Error al enviar formulario de contacto:', error);
      setSubmitResult({
        success: false,
        message: 'Hubo un problema al enviar tu mensaje. Por favor, intenta nuevamente o contáctanos directamente por teléfono.'
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData]);



  return (
    <main>
      {/* Hero Section */}
      <section className="position-relative" style={{ paddingTop: 'clamp(5rem, 10vw, 7rem)', minHeight: 'clamp(40vh, 50vh, 60vh)', backgroundColor: '#000000' }}>
        <div className="position-absolute w-100 h-100" style={{
          background: 'url(/images/contact/contact_vv_optimized.png) center/cover no-repeat',
          opacity: '0.5',
          top: 0,
          left: 0
        }}></div>
        <div className="position-absolute w-100 h-100" style={{
          background: 'linear-gradient(135deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.05) 100%)',
          top: 0,
          left: 0
        }}></div>
        <div className="container position-relative" style={{ zIndex: 1 }}>
          <div className="row min-vh-40 align-items-center py-5">
            <motion.div 
              className="col-lg-8 mx-auto text-center text-white"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 style={{ 
                fontFamily: 'Avenir Next Thai Modern, sans-serif', 
                fontWeight: 'bold', 
                fontSize: 'clamp(2.5rem, 5vw, 4rem)', 
                color: '#f8cc3d', 
                marginBottom: '1.5rem' 
              }}>
                Contáctanos
              </h1>
              <div style={{ marginBottom: '2rem', display: 'inline-block' }}>
                <h2 style={{ 
                  fontFamily: 'Avenir Next Thai Modern, sans-serif', 
                  fontWeight: 'normal', 
                  fontSize: 'clamp(1rem, 2vw, 1.5rem)', 
                  color: '#e6e5e5', 
                  textTransform: 'uppercase', 
                  letterSpacing: 'clamp(1px, 0.15vw, 2px)', 
                  marginBottom: '0.5rem' 
                }}>
                  NUESTRO EQUIPO Y SERVICIOS
                </h2>
                <div style={{ width: '100%', height: '2px', background: '#9fcfaa', marginTop: '0.5rem' }}></div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>



      {/* Contact Form Section */}
      <section className="py-5" style={{ backgroundColor: '#e0f0e5', opacity: 1 }}>
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                <div className="row g-0">
                  <div className="col-lg-5">
                    <div className="position-relative h-100">
                      <img
                        src="/images/contact/contact_vv_optimized.png"
                        alt="Contact Us"
                        className="w-100 h-100"
                        style={{ objectFit: 'cover' }}
                      />
                      <div className="position-absolute top-0 start-0 w-100 h-100"
                           style={{ background: 'linear-gradient(135deg, rgba(0,0,0,0.85) 0%, rgba(98,88,43,0.7) 100%)' }}>
                        <div className="d-flex flex-column justify-content-center h-100 text-white p-3 p-sm-4 p-md-5" style={{ maxWidth: '100%', overflowWrap: 'break-word' }}>
                          <h2 style={{ 
                            fontFamily: 'Avenir Next Thai Modern, sans-serif', 
                            fontWeight: 'bold', 
                            fontSize: 'clamp(1.25rem, 4vw, 1.75rem)', 
                            color: '#ffffff', 
                            marginBottom: '1rem',
                            wordBreak: 'break-word',
                            hyphens: 'auto',
                            lineHeight: '1.3'
                          }}>¿Listo para expandir tu práctica?</h2>
                           <p className="mb-3" style={{ lineHeight: '1.4' }}>
                            Comunícate con uno de nuestros expertos para:
                          </p>
                          <ul className="list-unstyled mb-0" style={{ 
                            fontSize: 'clamp(0.8rem, 1.8vw, 1rem)', 
                            paddingLeft: '0.5rem',
                            paddingRight: '0.5rem',
                            marginTop: '0.5rem',
                            lineHeight: '1.4'
                          }}>
                            <li className="mb-2 d-flex align-items-start">
                              <i className="bi bi-check2-circle me-2 mt-1" style={{ flexShrink: 0, fontSize: '0.9em' }}></i>
                              Orientarte sobre lo que debes saber antes de vender suplementos.
                            </li>
                            <li className="mb-2 d-flex align-items-start">
                              <i className="bi bi-check2-circle me-2 mt-1" style={{ flexShrink: 0, fontSize: '0.9em' }}></i>
                              Recibir una recomendación personalizada de acuerdo a tu práctica o negocio.
                            </li>
                            <li className="mb-2 d-flex align-items-start">
                              <i className="bi bi-check2-circle me-2 mt-1" style={{ flexShrink: 0, fontSize: '0.9em' }}></i>
                              Realizar un análisis de mercado para una recomendación de precios y ganancia.
                            </li>
                            <li className="mb-2 d-flex align-items-start">
                              <i className="bi bi-check2-circle me-2 mt-1" style={{ flexShrink: 0, fontSize: '0.9em' }}></i>
                              Conocer cómo se lleva a cabo el proceso de compra.
                            </li>
                            <li className="d-flex align-items-start">
                              <i className="bi bi-check2-circle me-2 mt-1" style={{ flexShrink: 0, fontSize: '0.9em' }}></i>
                              Realizar una re-compra de productos.
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-7">
                    <div className="p-4 p-md-5">
                      <h3 className="h4 fw-bold mb-4" style={{ marginTop: '0.5rem' }}>Formulario de Contacto</h3>
                      <p className="text-muted small mb-3"><span className="text-danger">*</span> Todos los campos son obligatorios</p>
                      {submitResult.message && (
                        <div className={`alert ${submitResult.success ? 'alert-success' : 'alert-danger'} mb-4`} role="alert">
                          {submitResult.message}
                        </div>
                      )}
                      
                      <form onSubmit={handleSubmit} className="mt-3">
                        <div className="row">
                          <div className="col-md-6 mb-4">
                            <label htmlFor="name" className="form-label mb-2">Nombre <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              className="form-control"
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              required
                              disabled={isSubmitting}
                            />
                          </div>
                          <div className="col-md-6 mb-4">
                            <label htmlFor="lastName" className="form-label mb-2">Apellido <span className="text-danger">*</span></label>
                            <input
                              type="text"
                              className="form-control"
                              id="lastName"
                              name="lastName"
                              value={formData.lastName || ''}
                              onChange={handleChange}
                              required
                              disabled={isSubmitting}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-md-6 mb-4">
                            <label htmlFor="email" className="form-label mb-2">Email <span className="text-danger">*</span></label>
                            <input
                              type="email"
                              className={`form-control ${formErrors.email ? 'is-invalid' : ''}`}
                              id="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              required
                              disabled={isSubmitting}
                            />
                            {formErrors.email && (
                              <div className="invalid-feedback">
                                {formErrors.email}
                              </div>
                            )}
                          </div>
                          <div className="col-md-6 mb-4">
                            <label htmlFor="phone" className="form-label mb-2">Teléfono <span className="text-danger">*</span></label>
                            <input
                              type="tel"
                              className="form-control"
                              id="phone"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              required
                              disabled={isSubmitting}
                            />
                          </div>
                        </div>
                        <div className="mb-3">
                          <label htmlFor="specialty" className="form-label mb-2">Especialidad/Negocio <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            className="form-control"
                            id="specialty"
                            name="specialty"
                            value={formData.specialty}
                            onChange={handleChange}
                            required
                            disabled={isSubmitting}
                          />
                        </div>
                        <div className="mb-3">
                          <label htmlFor="town" className="form-label mb-2">Pueblo <span className="text-danger">*</span></label>
                          <select
                            className="form-select"
                            id="town"
                            name="town"
                            value={formData.town || ''}
                            onChange={handleChange}
                            required
                            disabled={isSubmitting}
                          >
                            <option value="">Selecciona tu pueblo</option>
                            <option value="Adjuntas">Adjuntas</option>
                            <option value="Aguada">Aguada</option>
                            <option value="Aguadilla">Aguadilla</option>
                            <option value="Aguas Buenas">Aguas Buenas</option>
                            <option value="Aibonito">Aibonito</option>
                            <option value="Añasco">Añasco</option>
                            <option value="Arecibo">Arecibo</option>
                            <option value="Arroyo">Arroyo</option>
                            <option value="Barceloneta">Barceloneta</option>
                            <option value="Barranquitas">Barranquitas</option>
                            <option value="Bayamón">Bayamón</option>
                            <option value="Cabo Rojo">Cabo Rojo</option>
                            <option value="Caguas">Caguas</option>
                            <option value="Camuy">Camuy</option>
                            <option value="Canóvanas">Canóvanas</option>
                            <option value="Carolina">Carolina</option>
                            <option value="Cataño">Cataño</option>
                            <option value="Cayey">Cayey</option>
                            <option value="Ceiba">Ceiba</option>
                            <option value="Ciales">Ciales</option>
                            <option value="Cidra">Cidra</option>
                            <option value="Coamo">Coamo</option>
                            <option value="Comerío">Comerío</option>
                            <option value="Corozal">Corozal</option>
                            <option value="Culebra">Culebra</option>
                            <option value="Dorado">Dorado</option>
                            <option value="Fajardo">Fajardo</option>
                            <option value="Florida">Florida</option>
                            <option value="Guánica">Guánica</option>
                            <option value="Guayama">Guayama</option>
                            <option value="Guayanilla">Guayanilla</option>
                            <option value="Guaynabo">Guaynabo</option>
                            <option value="Gurabo">Gurabo</option>
                            <option value="Hatillo">Hatillo</option>
                            <option value="Hormigueros">Hormigueros</option>
                            <option value="Humacao">Humacao</option>
                            <option value="Isabela">Isabela</option>
                            <option value="Jayuya">Jayuya</option>
                            <option value="Juana Díaz">Juana Díaz</option>
                            <option value="Juncos">Juncos</option>
                            <option value="Lajas">Lajas</option>
                            <option value="Lares">Lares</option>
                            <option value="Las Marías">Las Marías</option>
                            <option value="Las Piedras">Las Piedras</option>
                            <option value="Loíza">Loíza</option>
                            <option value="Luquillo">Luquillo</option>
                            <option value="Manatí">Manatí</option>
                            <option value="Maricao">Maricao</option>
                            <option value="Maunabo">Maunabo</option>
                            <option value="Mayagüez">Mayagüez</option>
                            <option value="Moca">Moca</option>
                            <option value="Morovis">Morovis</option>
                            <option value="Naguabo">Naguabo</option>
                            <option value="Naranjito">Naranjito</option>
                            <option value="Orocovis">Orocovis</option>
                            <option value="Patillas">Patillas</option>
                            <option value="Peñuelas">Peñuelas</option>
                            <option value="Ponce">Ponce</option>
                            <option value="Quebradillas">Quebradillas</option>
                            <option value="Rincón">Rincón</option>
                            <option value="Río Grande">Río Grande</option>
                            <option value="Sabana Grande">Sabana Grande</option>
                            <option value="Salinas">Salinas</option>
                            <option value="San Germán">San Germán</option>
                            <option value="San Juan">San Juan</option>
                            <option value="San Lorenzo">San Lorenzo</option>
                            <option value="San Sebastián">San Sebastián</option>
                            <option value="Santa Isabel">Santa Isabel</option>
                            <option value="Toa Alta">Toa Alta</option>
                            <option value="Toa Baja">Toa Baja</option>
                            <option value="Trujillo Alto">Trujillo Alto</option>
                            <option value="Utuado">Utuado</option>
                            <option value="Vega Alta">Vega Alta</option>
                            <option value="Vega Baja">Vega Baja</option>
                            <option value="Vieques">Vieques</option>
                            <option value="Villalba">Villalba</option>
                            <option value="Yabucoa">Yabucoa</option>
                            <option value="Yauco">Yauco</option>
                          </select>
                        </div>
                        <div className="mb-3">
                          <label htmlFor="message" className="form-label mb-2">Mensaje <span className="text-danger">*</span></label>
                          <textarea
                            className="form-control"
                            id="message"
                            name="message"
                            rows="4"
                            value={formData.message}
                            onChange={handleChange}
                            required
                            disabled={isSubmitting}
                            placeholder="Escribe tu mensaje aquí..."
                          ></textarea>
                        </div>
                        <button 
                          type="submit" 
                          className="btn w-100" 
                          style={{ backgroundColor: '#000000', color: '#ffffff', fontFamily: 'Avenir Next Thai Modern, sans-serif', fontWeight: 'bold' }}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Enviando...
                            </>
                          ) : 'Enviar Mensaje'}
                        </button>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Information Cards Section */}
      <section className="py-5 bg-light">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-5 mb-4 mb-lg-0">
              <div className="card h-100 shadow-sm border-0">
                <div className="card-body p-4 text-center">
                  <div className="mb-3">
                    <a href="https://www.instagram.com/VVHealthPR" target="_blank" rel="noopener noreferrer">
                      <img src="/images/instagram-icon.svg" alt="Instagram" className="img-fluid" style={{ width: '40px', height: '40px' }} />
                    </a>
                  </div>
                  <p className="mb-4">
                    Conoce más sobre nosotros y los beneficios que las vitaminas aportan a tu salud a través de nuestra cuenta de Instagram.
                  </p>
                  <h5 className="text-primary">
                    <a href="https://www.instagram.com/VVHealthPR" target="_blank" rel="noopener noreferrer" style={{ color: '#6f42c1', textDecoration: 'none' }}>
                      @VVHealthPR
                    </a>
                  </h5>
                </div>
              </div>
            </div>
            <div className="col-lg-5">
              <div className="card h-100 shadow-sm border-0">
                <div className="card-body p-4 text-center">
                  <div className="mb-3">
                    <a href="mailto:info@VVHealthPR.com">
                      <img src="/images/email-icon.svg" alt="Email" className="img-fluid" style={{ width: '40px', height: '40px' }} />
                    </a>
                  </div>
                  <p className="mb-4">
                    Si deseas ser parte del equipo o trabajar en una idea colaborativa, no dudes en comunicarte para ir por un café.
                  </p>
                  <h5 className="text-primary">
                    <a href="mailto:info@VVHealthPR.com" style={{ color: '#0d6efd', textDecoration: 'none', backgroundColor: '#e6f0ff', padding: '2px 8px', borderRadius: '4px' }}>
                      info@VVHealthPR.com
                    </a>
                  </h5>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

// Memoize the component to prevent unnecessary re-renders
// Memorizar el componente para evitar renderizados innecesarios
export default memo(Contact);
